import React, { useState } from 'react';

import classNames from 'classnames';
import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { faAlarmExclamation, faEllipsisV } from '@fortawesome/pro-regular-svg-icons';
import { faAsterisk } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Assignee from './assignee';
import Attachments from './attachments';
import DeleteModal from 'bundles/apex/tasks/delete_modal';
import Dropdown from 'components/dropdown';
import Parent from './parent';
import Status from './status';
import TaskType from './task_type';
import Tooltip from 'components/tooltip';
import { formatDate } from 'helpers/formatters/date_time_formats';
import { nonClosingProps } from 'bundles/apex/tasks/task_modal/helpers';

import type { Attachment, Task } from 'bundles/apex/tasks/types';
import type { Parent as ParentType } from 'bundles/crm/types';

export interface Props {
  active?: boolean;
  addTaskAttachment: (task: Task, attachment: Attachment) => void;
  attachmentsPath: string;
  className?: string;
  currentParent?: ParentType;
  deleteTaskAttachment: any;
  features?: {
    dromoTaskImport?: boolean;
    marketingListings?: boolean;
    taskAttachmentExceptions?: boolean;
  };
  hideAssignee?: boolean;
  modalOpen?: boolean;
  showParents?: boolean | 'path';
  task: Task;
  onClick?: (task: Task) => void;
  onDelete: (task: Task) => void;
  onUpdate: (task: Task) => void;
}

export default function TaskRow({
  active,
  addTaskAttachment,
  attachmentsPath,
  className,
  currentParent,
  deleteTaskAttachment,
  features,
  hideAssignee,
  modalOpen,
  showParents,
  task,
  onClick,
  onDelete,
  onUpdate,
}: Props) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  return <>
    <div
      {...nonClosingProps}
      className={classNames(
        'd-flex align-items-start gap-2 clickable py-2 px-1 border-bottom',
        { 'apex-tasks-list__task--active': active },
        className
      )}
      onClick={onClick && (() => onClick(task))}
    >
      <Status
        addTaskAttachment={addTaskAttachment}
        attachmentsPath={attachmentsPath}
        deleteTaskAttachment={deleteTaskAttachment}
        features={features}
        task={task}
        onUpdate={onUpdate}
      />
      <div className="d-flex align-items-start gap-2 flex-grow-1" style={{ minWidth: 0 }}>
        <div className="d-flex flex-column gap-1 flex-grow-1" style={{ minWidth: 0 }}>
          <div className="text-truncate fw-bold">
            <Tooltip content={task.name} onlyOnOverflow>
              {task.name}
              {task.required &&
                <Tooltip content="Required for voucher submission">
                  <FontAwesomeIcon className="ms-1 text-danger fs-xs" icon={faAsterisk} />
                </Tooltip>
              }
            </Tooltip>
          </div>
          <div className="d-flex gap-2">
            {showParents &&
              <Parent
                currentParent={currentParent}
                modalOpen={modalOpen}
                task={task}
              />
            }
            <TaskType task={task} />
          </div>
          {(task.deadline && task.deadline.date) && (
            <div className="text-muted">Due: {formatDate(task.deadline.date)}</div>
          )}
        </div>

        <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
          {!hideAssignee && !task.parent?.private && <Assignee assignee={task.assignee} />}
          <div className="d-flex gap-2 align-items-center">
            {!task.completed && task.deadline?.date && new Date(task.deadline.date) < new Date() &&
              <Badge className="bg-solid-pink-50 text-solid-pink-950" variant="secondary">
                <FontAwesomeIcon icon={faAlarmExclamation} size="sm" />Overdue
              </Badge>
            }
            <Attachments task={task} />
          </div>
        </div>
      </div>
      <div onClick={e => e.stopPropagation()}>
        <Dropdown icon={faEllipsisV}>
          <Dropdown.Item
            isDelete
            onClick={() => setShowDeleteModal(true)}
          />
        </Dropdown>
      </div>
    </div>

    <DeleteModal
      show={showDeleteModal}
      onDelete={() => onDelete(task)}
      onHide={() => setShowDeleteModal(false)}
    />
  </>;
}
